import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectApplicationName } from '../../selectors';
import { useSiteConfig } from '../../contexts';
import { useGlobalization } from '../../contexts/GlobalizationProvider/useGlobalization';
import { useGetCurrentApp } from '../useGetCurrentApp';
import { useProjects } from '../useProjects';
import { useAccessToken } from '../authStates';
import {
	getUserInfoV2,
	getAutodeskContext,
} from '../../services/forge.service';
import { getCurrentCompanySettings } from '../../utils/companySettings';
import useVerifyUserAccess from '../../contexts/Forge/hooks/useVerifyUserAccess';
import { useInitialization } from './useInitialization';
import { buildInitializationSteps } from './useInitializationSteps';

interface AppInitializerProps {
	isLandingPage?: boolean;
}

/**
 * Runs the one-time application initialization sequence (config → auth →
 * identity → flags → data) from a high, structurally-stable mount point.
 *
 * It is mounted as a child of SiteConfigProvider — an ancestor of the
 * auth / feature-flag / settings / analytics / loading providers. Those lower
 * providers can remount their subtree (e.g. when a feature flag flips during
 * the flags phase, which changes a conditional provider's element structure);
 * hosting the initializer above them guarantees init() runs exactly once rather
 * than restarting on every such remount. It renders nothing.
 *
 * All dynamic dependencies are exposed to the step closures via getters that
 * read refs, so this can mount before config/identity have resolved without
 * freezing stale values.
 */
export const AppInitializer = ({
	isLandingPage = false,
}: AppInitializerProps): null => {
	const dispatch = useDispatch();
	const { config } = useSiteConfig();
	const { i18n } = useGlobalization();
	const appName = useSelector(selectApplicationName);
	const currentApp = useGetCurrentApp();
	const { activeProject } = useProjects();
	const accessToken = useAccessToken();
	const { handleFailedInitialization } = useVerifyUserAccess();

	// Refs so the (built-once) step closures read the latest values at
	// await-time rather than the values present at first render.
	const configRef = useRef(config);
	configRef.current = config;
	const appNameRef = useRef(appName);
	appNameRef.current = appName;
	const currentAppRef = useRef(currentApp);
	currentAppRef.current = currentApp;
	const activeProjectRef = useRef(activeProject);
	activeProjectRef.current = activeProject;
	const accessTokenRef = useRef(accessToken);
	accessTokenRef.current = accessToken;

	const stepsRef = useRef(
		buildInitializationSteps({
			dispatch,
			isLandingPage,
			// Captured once at mount: a tenant switch boots on /projects/<id>.
			isProjectInjectionLoad:
				window.location.pathname.includes('/projects/'),
			i18n,
			getConfig: () => configRef.current,
			getAppName: () => appNameRef.current,
			getCurrentApp: () => currentAppRef.current,
			getActiveProject: () => activeProjectRef.current,
			getAccessToken: () => accessTokenRef.current,
			services: {
				getUserInfoV2,
				getAutodeskContext,
				getCurrentCompanySettings,
			},
		}),
	);

	const { init, phase } = useInitialization(stepsRef.current);
	useEffect(() => {
		init();
	}, [init]);

	// Note on tenant switching: every project/tenant change in this app is a
	// full page reload (ProjectPicker → window.location; emulation → hard
	// redirect), so there is no in-page project change to react to. On a
	// /projects/<sourceId> (switch) load, init's identity already waits for the
	// target project and fetches its context directly (see isProjectInjectionLoad
	// in useInitializationSteps), so no separate re-fetch is needed.

	// Orphaned-user / init-failure routing: a failed init (no provisioning,
	// stale token, context error) sets phase 'error'. Route it into the
	// user-team-access verification, which either shows the "no access" dialog
	// or logs the user out — matching the pre-consolidation behaviour where
	// identity errors were caught in Forge. Without this the app would hang on
	// the loading gate. Guarded to run once.
	const orphanHandledRef = useRef(false);
	useEffect(() => {
		if (phase === 'error' && !orphanHandledRef.current) {
			orphanHandledRef.current = true;
			handleFailedInitialization();
		}
	}, [phase, handleFailedInitialization]);

	return null;
};

export default AppInitializer;
